import { Input, Label } from '@artstorefronts/ui';
import type { AdminBounty } from './bounty-shared';

/**
 * REACH BACK (#4118) — on a series bounty's first publish, it may also pay members who
 * signed up in the last few days, never more than its own day, so a member already past
 * that day never gets it. Only what they do from now on counts. The server enforces the
 * same limits and refuses steps that count what a member already has.
 */

/** Whether publishing this bounty sets its cutoff, so reach back applies. */
export function canReachBack(bounty: Pick<AdminBounty, 'seriesKey' | 'seriesDay' | 'eligibleSignupFrom'>): boolean {
  return Boolean(bounty.seriesKey) && !bounty.eligibleSignupFrom && (bounty.seriesDay ?? 0) > 0;
}

/** The days typed, as the mutation wants them: a whole number, or 0 for blank. */
export function reachBackValue(text: string): number {
  return text.trim() ? Number(text) : 0;
}

/**
 * The publish confirm's sentence for a series bounty. `when` is "now" for Go live, or
 * "then" for a scheduled go-live, whose cutoff is the moment it goes live.
 */
export function seriesPublishLine(
  bounty: Pick<AdminBounty, 'pointsReward' | 'seriesDay'> & Partial<Pick<AdminBounty, 'kind'>>,
  days: number,
  when: 'now' | 'then' = 'now'
): string {
  // A message or card item pays only on an open or a connect made after it lands.
  if (bounty.kind && bounty.kind !== 'task') {
    const reach = days
      ? `every member who signs up from ${when} on, and members who signed up in the ${days} day${days === 1 ? '' : 's'} before who are not past day ${bounty.seriesDay}`
      : `every member who signs up from ${when} on`;
    return `It reaches ${reach}, on day ${bounty.seriesDay}.${bounty.pointsReward > 0 ? ` It pays ${bounty.pointsReward} points when they act on it.` : ''}`;
  }
  if (!days) {
    return `It pays ${bounty.pointsReward} points to every member who signs up from ${when} on and finishes it. Members who joined before ${when} are never paid by it.`;
  }
  return `It pays ${bounty.pointsReward} points to every member who signs up from ${when} on, and to members who signed up in the ${days} day${days === 1 ? '' : 's'} before — none of them past day ${bounty.seriesDay} — for finishing it from ${when} on. Nothing they did before ${when} counts.`;
}

export function SeriesReachBackField({
  bounty,
  value,
  onChange,
}: {
  bounty: Pick<AdminBounty, 'seriesDay'>;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="mt-3 space-y-1 text-left" data-testid="bounty-reach-back">
      <Label htmlFor="bounty-reach-back">Also include members who signed up in the last … days</Label>
      <Input
        id="bounty-reach-back"
        inputMode="numeric"
        placeholder="0"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <p className="text-xs text-gray-500">
        Blank or 0: new signups only. At most {bounty.seriesDay}, so no one already past day {bounty.seriesDay} gets it.
      </p>
    </div>
  );
}
